import React, { PropTypes } from "react";
import { Field, change as changeFieldValue } from "redux-form";
import { FormattedMessage } from "react-intl";

import { RectangleSpinner } from "../../../../../../common/spinners";
import { Render } from "../../../../../../common";
import { TextInput, StyledCheckboxInput, SelectInput } from "../../../../../../common/form";
import { AGENT, EXTERNAL_ROLES } from "../../../../../../../utils/Roles";
import memberCoverageGuaranteeTypes, { WAIVED } from "./enums/memberCoverageGuaranteeTypes";
import { MEMBER, MEMBER_MULTIPLIER, PRICE_LIST } from "../../../../../admin/guarantees/enums/guaranteeTypes";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import userHasAccess from "../../../../../../../utils/userHasAccess";

class RenderGuarantees extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = { previousSelectedCoverageType: [] };
    this.forceNullOnEmpty = this.forceNullOnEmpty.bind(this);
    this.toggleSelection = this.toggleSelection.bind(this);
    this.renderPerMemberConfigs = this.renderPerMemberConfigs.bind(this);
    this.renderFormulaConfigs = this.renderFormulaConfigs.bind(this);
    this.isUserAllowedToModifyGuaranteeConfig = this.isUserAllowedToModifyGuaranteeConfig.bind(this);
    
    this._firstNewMemberLoad = true;
    this.isExternalUser = userHasAccess(EXTERNAL_ROLES, true);
  }

  componentWillMount() {
    this.props.fields.removeAll();

    const { guarantees } = this.props;

    this.orderGuaranteesList(guarantees).map((guarantee) => {
      this.props.fields.push({
        guaranteeId: guarantee.guaranteeId,
        checked: guarantee.checked,
        coverageType: guarantee.coverageType,
        ageOfPolicyDecrease: guarantee.ageOfPolicyDecrease,
        ageOfPolicyExpiration: guarantee.ageOfPolicyExpiration,
        formulaMspa: guarantee.formulaMspa,
        volume: guarantee.volume,
        invoicedClientRate: guarantee.invoicedClientRate,
        allowance: guarantee.allowance,
        supplierRate: guarantee.supplierRate,
        guarantee: guarantee.guarantee,
        allowanceBlur: guarantee.allowanceBlur,
      });
    });
  }

  componentWillReceiveProps(nextProps) {
    const guaranteesIdentical =
      this.props.guarantees.every((guarantee) =>
        nextProps.guarantees.some(
          (newGuarantee) =>
            newGuarantee.clientCoverageId == guarantee.clientCoverageId
        )
      ) &&
      nextProps.guarantees.every((newGuarantee) =>
        this.props.guarantees.some(
          (guarantee) =>
            newGuarantee.clientCoverageId == guarantee.clientCoverageId
        )
      );

    const {
      classId,
      memberId,
      fields
    } = this.props;

    const classChanged = classId !== nextProps.classId;
    const memberChanged = memberId !== nextProps.memberId;

    if (!guaranteesIdentical || classChanged || memberChanged || this._firstNewMemberLoad ) {
      // this._firstNewMemberLoad is used for memberCoverage view, when none of the previous conditions are met
      // during the first guarantee load
      fields.removeAll();
      
      this.orderGuaranteesList(nextProps.guarantees).map((guarantee) => {
        const isChecked = guarantee.checked;
        const coverageType = !isChecked ? WAIVED.value : guarantee.coverageType ? guarantee.coverageType : null;
        fields.push({ ...guarantee, checked: isChecked, coverageType: coverageType });
      });

      this._firstNewMemberLoad = false;
    }
  }

  componentWillUnmount() {
    this.props.fields.removeAll();
  }

  orderGuaranteesList(guarantees) {
    return [...guarantees].sort((a, b) => 
      (a.name).localeCompare(b.name)
    );
  }

  forceNullOnEmpty(value) {
    return value === "" ? null : value;
  }

  isMemberGuarantee(guaranteeType) {
    return [MEMBER.value, MEMBER_MULTIPLIER.value].includes(guaranteeType);
  }

  isUserAllowedToModifyGuaranteeConfig(guaranteeType) {
    return !this.isExternalUser || !this.isMemberGuarantee(guaranteeType);
  }


  toggleSelection(index, guaranteeId, event) {
    const matchedGuarantee = this.props.allMemberCoverageGuarantees.find(obj => obj.guaranteeId == guaranteeId);
    const previousSelectedCoverageType = [...this.state.previousSelectedCoverageType];
    const checked = event.target.checked;
    const coverageTypeFieldName = `memberCoverageGuarantees[${index}].coverageType`;
    /**
     * If user unchecks checkbox, we change to WAIVED type,
     * if user rechecks the checkbox, we check if there's a matching value in the state (previousSelectedCoverageType),
     * if yes, we change it to this value, if not, we set to NULL;
     */
    if(!checked) {
      previousSelectedCoverageType[index] = matchedGuarantee && matchedGuarantee.coverageType;
      this.props.changeFieldValue(this.props.formName, coverageTypeFieldName, WAIVED.value);
      this.setState({ previousSelectedCoverageType });
    }
    else if(this.state.previousSelectedCoverageType && index in this.state.previousSelectedCoverageType) {
      this.props.changeFieldValue(this.props.formName, coverageTypeFieldName, this.state.previousSelectedCoverageType[index]);
    }
    else {
      this.props.changeFieldValue(this.props.formName, coverageTypeFieldName, null);
    }
  }

  renderPerMemberConfigs(guarantee, field) {
    return (
      <div className="guarantee-row extra-config config-fields formula-fields">
        <Field
          id={`${guarantee}.volume`}
          name={`${guarantee}.volume`}
          component={TextInput}
          type="absNumber"
          normalize={this.forceNullOnEmpty}
          label={<FormattedMessage id="memberCoverages.form.volume" />}
          disabled={this.props.readOnly}
          required={!!this.props.volumeRequired}
        />
        <Field
          id={`${guarantee}.invoicedClientRate`}
          name={`${guarantee}.invoicedClientRate`}
          component={TextInput}
          type="absNumber"
          normalize={this.forceNullOnEmpty}
          label={
            <FormattedMessage
              id={`clientCoverages.form.invoicedClientRate.${this.props.ratePeriodType}`}
            />
          }
          disabled={this.props.readOnly}
          required={!!this.props.invoicedClientRateRequired}
        />
        <Field
          id={`${guarantee}.allowance`}
          name={`${guarantee}.allowance`}
          component={TextInput}
          type="absNumber"
          onBlur={field.allowanceBlur.bind(null, `${guarantee}.supplierRate`)}
          normalize={this.forceNullOnEmpty}
          label={
            <FormattedMessage
              id={`memberCoverages.form.allowance.${this.props.ratePeriodType}`}
            />
          }
          disabled={this.props.readOnly}
          required={!!this.props.allowanceRequired}
        />
        <Render for={AGENT}>
          <Field
            id={`${guarantee}.supplierRate`}
            name={`${guarantee}.supplierRate`}
            component={TextInput}
            type="absNumber"
            normalize={this.forceNullOnEmpty}
            label={
              <FormattedMessage
                id={`memberCoverages.form.supplierRate.${this.props.ratePeriodType}`}
              />
            }
            readOnly={true}
          />
        </Render>
      </div>
    );
  }

  renderFormulaConfigs(guarantee) {
    return (
      <div className="guarantee-row extra-config config-fields">
        <Field
          id={`${guarantee}.formulaMspa`}
          name={`${guarantee}.formulaMspa`}
          component={TextInput}
          type="absNumber"
          normalize={this.forceNullOnEmpty}
          label={<FormattedMessage id="memberCoverages.form.formulaMspa" />}
          disabled={this.props.readOnly}
        />
      </div>
    );
  }

  render() {
    if (!this.props.fetched || this.props.guarantees.length != this.props.fields.length) {
      return <RectangleSpinner />;
    }
    if (this.props.fetched && this.props.guarantees.length == 0) {
      return (
        <div className="danger">
          <FormattedMessage id="memberCoverages.form.noGuarantees" />
        </div>
      );
    }

    const fields = this.props.fields.map((fieldName, index, fields) => {
      const field = fields.get(index);
      const guaranteeType = field.guarantee.guaranteeType;
      const isDisabled = this.props.readOnly || !this.isUserAllowedToModifyGuaranteeConfig(guaranteeType);

      return (
        <div key={index} className="guarantee-options">
          <div className="guarantee-row">
            <div className="guarantee checkbox-dropdown">
              <Field
                id={`${fieldName}.checked`}
                name={`${fieldName}.checked`}
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: field.guaranteeId }}
                label={field.name}
                props={{ onChange: this.toggleSelection.bind(null, index, field.guaranteeId) }}
                disabled={isDisabled} 
              />
              {guaranteeType == PRICE_LIST.value &&
                <Field
                  id={`${fieldName}.coverageType`}
                  name={`${fieldName}.coverageType`}
                  component={SelectInput}
                  required={true}
                  options={memberCoverageGuaranteeTypes}
                  disabled={isDisabled || !field.checked}
                  label={<FormattedMessage id="memberCoverages.form.coverageType" />}
                />
              }
            </div>
            {!this.isExternalUser && (
              <div className="options age-options">
                <Field
                  id={`${fieldName}.ageOfPolicyExpiration`}
                  name={`${fieldName}.ageOfPolicyExpiration`}
                  label={<FormattedMessage id="memberCoverages.form.ageOfPolicyExpiration" />}
                  component={TextInput}
                  type="absNumber"
                  disabled={
                    this.props.readOnly || !field.checked
                  }
                  placeholder={field.placeHolderExpiration}
                  normalize={this.forceNullOnEmpty}
                  required={this.props.ageExpirationRequired}
                />
                <Field
                  id={`${fieldName}.ageOfPolicyDecrease`}
                  name={`${fieldName}.ageOfPolicyDecrease`}
                  label={<FormattedMessage id="memberCoverages.form.ageOfPolicyDecrease" />}
                  component={TextInput}
                  type="absNumber"
                  disabled={
                    this.props.readOnly || !field.checked
                  }
                  placeholder={field.placeHolderDecrease}
                  normalize={this.forceNullOnEmpty}
                  required={this.props.ageDecreaseRequired}
                />
              </div>
            )}
          </div>
          {
            !this.isExternalUser && 
            field.isTypeMember &&
            field.checked && 
            this.renderPerMemberConfigs(fieldName, field)
          }
          {
            !this.isExternalUser && 
            guaranteeType == "FORMULA" &&
            field.checked &&
            this.renderFormulaConfigs(fieldName)
          }
        </div>
      );
    });

    return (
    <div>
      {fields}
    </div>
    );
  }
}

RenderGuarantees.propTypes = {
  add: PropTypes.bool,
  fetched: PropTypes.bool,
  initValues: PropTypes.object,
  guarantees: PropTypes.array,
  classId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fields: PropTypes.object,
  firstCoverage: PropTypes.bool,
  meta: PropTypes.object,
  readOnly: PropTypes.bool,
  ratePeriodType: PropTypes.string,
  formName: PropTypes.string,
  changeFieldValue: PropTypes.func,
  allMemberCoverageGuarantees: PropTypes.array,
  volumeRequired: PropTypes.bool,
  invoicedClientRateRequired: PropTypes.bool,
  allowanceRequired: PropTypes.bool,
  supplierRateRequired: PropTypes.bool,
  ageExpirationRequired: PropTypes.bool,
  ageDecreaseRequired: PropTypes.bool,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  addType: PropTypes.string,
  latestCoverage: PropTypes.object,
  nextCoverage: PropTypes.object,
  initialValues: PropTypes.object
};

function mapStateToProps(state, ownProps) {
  const formName = ownProps.formName;
    return {
        allMemberCoverageGuarantees: state.form && state.form[formName] && state.form[formName].values && state.form[formName].values.memberCoverageGuarantees
    };
}

function mapDispatchToProps(dispatch) {
    return {
        changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(RenderGuarantees);


// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/members/member/coverage/RenderGuarantees.js